import { useCallback, useEffect, useState } from 'react'
import {
  detectCountryApi,
  type DetectedCountry,
  type DetectCountryResponse,
  type DetectedPlan,
} from '@/lib/api'

export type { DetectedCountry, DetectedPlan, DetectCountryResponse }

export interface DetectedCountryResult {
  country: DetectedCountry | null
  plan: DetectedPlan | null
  enterprise_plan: DetectedPlan | null
  loading: boolean
  error: string | null
  /** Active manual override (ISO-2) — null while auto-detecting. */
  overrideIso2: string | null
  /** Pick a country manually (ISO-2), or null to return to auto-detect. */
  setOverride: (iso2: string | null) => void
}

const STORAGE_KEY = 'peerpass_detected_country'
/** Persists a manual pricing-country pick so signup inherits it too. */
const OVERRIDE_KEY = 'peerpass_detected_country_override'

interface CachedValue {
  country: DetectedCountry | null
  plan: DetectedPlan | null
  enterprise_plan: DetectedPlan | null
}

function readCache(): CachedValue | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as CachedValue
  } catch {
    // Stale/corrupt cache — refetch.
    return null
  }
}

function writeCache(value: CachedValue): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Storage full/blocked — detection still works for this session.
  }
}

function readOverride(): string | null {
  try {
    return sessionStorage.getItem(OVERRIDE_KEY)
  } catch {
    return null
  }
}

/**
 * Best-effort country detection for the public pricing/signup pages.
 *
 * Progressive enhancement: while detection is in flight the hook returns
 * `loading: true` with null country/plans, so pages render EUR defaults
 * before JavaScript hydrates (no layout shift). Once resolved, the result
 * (country + Growth/Enterprise monthly plans) is cached in sessionStorage
 * so navigation between /pricing and /signup triggers no second request.
 *
 * Manual override (pricing dropdown, DESIGN_CountryDetection.md §9.3):
 * `setOverride('MX')` refetches with `?iso_2=MX` (which beats every hint on
 * the API) and persists the pick in sessionStorage, so /signup — and the
 * `country_id` sent at registration — uses the same country the user saw on
 * /pricing. `setOverride(null)` returns to auto-detection. An override in
 * flight keeps the previous country/plans rendered (no flicker to EUR
 * defaults); only the first load shows the pulse.
 *
 * Failures degrade silently — callers keep their EUR defaults.
 */
export function useDetectedCountry(): DetectedCountryResult {
  const [overrideIso2, setOverrideIso2] = useState<string | null>(readOverride)
  const [result, setResult] = useState<Omit<DetectedCountryResult, 'loading' | 'overrideIso2' | 'setOverride'>>({
    country: null,
    plan: null,
    enterprise_plan: null,
    error: null,
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    // ── Manual override: fetch the chosen country's plans directly ──
    if (overrideIso2) {
      // Reuse the cache when it already holds this override's result.
      const cached = readCache()
      if (cached?.country?.iso_2 === overrideIso2) {
        setResult({ ...cached, error: null })
        setLoading(false)
        return
      }

      async function fetchOverride() {
        try {
          const data = await detectCountryApi({ iso_2: overrideIso2 ?? undefined })
          if (cancelled) return
          const value: CachedValue = {
            country: data.country ?? null,
            plan: data.plan ?? null,
            enterprise_plan: data.enterprise_plan ?? null,
          }
          setResult({ ...value, error: null })
          // Cache the override result so /signup (and pricing remounts) reuse it.
          if (value.country) writeCache(value)
        } catch (err) {
          if (cancelled) return
          // Keep the previously rendered country/plans: an override failure
          // must not flicker the page to EUR defaults or hide the dropdown
          // (the persisted override is still recoverable via auto-detect).
          setResult((prev) => ({
            ...prev,
            error: err instanceof Error ? err.message : 'Country detection failed',
          }))
        } finally {
          if (!cancelled) setLoading(false)
        }
      }

      setLoading(true)
      fetchOverride()
      return () => {
        cancelled = true
      }
    }

    // ── Auto-detect (no override) ──
    // sessionStorage is per-tab and cleared on tab close — fresh detection
    // every visit, no cookie-consent implications (see DESIGN_CountryDetection.md).
    const cached = readCache()
    if (cached) {
      setResult({ ...cached, error: null })
      setLoading(false)
      return
    }

    async function detect() {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        const locale = typeof navigator !== 'undefined' ? navigator.language : undefined

        const data = await detectCountryApi({
          ...(tz ? { tz } : {}),
          ...(locale ? { locale } : {}),
        })

        if (cancelled) return

        const value: CachedValue = {
          country: data.country ?? null,
          plan: data.plan ?? null,
          enterprise_plan: data.enterprise_plan ?? null,
        }
        setResult({ ...value, error: null })
        // Cache only a successful detection. A transient failure or a hard
        // fallback (data.fallback) must not be pinned for the whole tab
        // session — the next page load should retry.
        if (value.country) writeCache(value)
      } catch (err) {
        if (cancelled) return
        setResult({
          country: null,
          plan: null,
          enterprise_plan: null,
          error: err instanceof Error ? err.message : 'Country detection failed',
        })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    detect()
    return () => {
      cancelled = true
    }
  }, [overrideIso2])

  const setOverride = useCallback((iso2: string | null) => {
    const normalized = iso2 ? iso2.trim().toUpperCase() : null
    setOverrideIso2((prev) => (prev === normalized ? prev : normalized))
    try {
      if (normalized) {
        sessionStorage.setItem(OVERRIDE_KEY, normalized)
      } else {
        sessionStorage.removeItem(OVERRIDE_KEY)
      }
      // Invalidate the detection cache so a cleared override re-detects
      // instead of resurrecting the previous pick.
      sessionStorage.removeItem(STORAGE_KEY)
    } catch {
      // Storage blocked — the in-memory override still works this session.
    }
  }, [])

  return { ...result, loading, overrideIso2, setOverride }
}
